<script setup>
import { computed } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeTab: {
    type: String,
    default: 'me',
  },
});

const emit = defineEmits(['chatTabChange']);

const activeTabIndex = computed(() => {
  return props.items.findIndex(item => item.key === props.activeTab);
});

const onTabChange = key => {
  if (key !== props.activeTab) {
    emit('chatTabChange', key);
  }
};

const formatCount = count => {
  if (count === undefined || count === null) return '0';
  if (count > 999) return '999+';
  return String(count);
};

const keyboardEvents = {
  'Alt+KeyN': {
    action: () => {
      const nextIndex = (activeTabIndex.value + 1) % props.items.length;
      if (props.items[nextIndex]) {
        onTabChange(props.items[nextIndex].key);
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div class="dc-tabs-wrap">
    <div class="dc-tabs-scroll">
      <button
        v-for="item in items"
        :key="item.key"
        v-tooltip.bottom="item.name"
        class="dc-tab"
        :class="[
          item.key === activeTab ? 'dc-tab--active' : '',
          'dc-tab--' + item.key,
        ]"
        @click="onTabChange(item.key)"
      >
        <span :class="[item.icon, 'dc-tab-icon']" />
        <span v-if="item.count !== undefined" class="dc-tab-count">
          {{ formatCount(item.count) }}
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Respiro acima dos chips e divisoria fechando a faixa antes da lista. */
.dc-tabs-wrap {
  padding: 10px 8px;
  overflow: hidden;
  border-bottom: 1px solid #e9eaee;
}

.dark .dc-tabs-wrap {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.dc-tabs-scroll {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.dc-tab {
  /* Cor propria de cada aba; cada .dc-tab--<key> sobrescreve --tab-color. */
  --tab-color: #6b7280;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px 0;
  border-radius: 5px;
  border: 1.5px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  flex: 1 1 0;
  min-width: 0;
  min-height: 26px;
  justify-content: center;
}

.dark .dc-tab {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}

/* Hover apenas insinua a cor da aba; o preenchimento continua neutro. */
.dc-tab:hover {
  background: #f9fafb;
  border-color: color-mix(in srgb, var(--tab-color) 45%, #d1d5db);
}

.dark .dc-tab:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: color-mix(in srgb, var(--tab-color) 45%, rgba(255, 255, 255, 0.18));
}

/* Estado ativo: so a borda assume a cor do icone, o fundo segue neutro. */
.dc-tab--active {
  border-color: var(--tab-color);
}

.dark .dc-tab--active {
  border-color: var(--tab-color);
}

.dc-tab-icon {
  font-size: 12px;
  flex-shrink: 0;
  color: var(--tab-color);
}

.dc-tab-count {
  font-size: 9.5px;
  font-weight: 600;
  color: #374151;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.dark .dc-tab-count {
  color: #d1d5db;
}

/* ── Cor de cada aba: vale para o icone e para a borda quando ativa ── */
.dc-tab--unassigned { --tab-color: #f0724a; }
.dc-tab--waiting    { --tab-color: #f0a92c; }
.dc-tab--me         { --tab-color: #4a90e2; }
.dc-tab--automation { --tab-color: #8b5cf6; }
.dc-tab--resolved   { --tab-color: #22c55e; }
.dc-tab--groups     { --tab-color: #5566e8; }
</style>
